import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Lock, Inbox } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useT } from '../i18n';
import { formatMoneyShort, formatNumber, funnelConversionPct } from '../lib/reportFormat';
import type { Confidence, PipelineReport, ProductType, Stage } from '../types/api';
import { routeStages } from '../types/api';
import {
  AttentionPanel,
  BasketDrillIn,
  BasketPanel,
  Button,
  EmptyState,
  FunnelChart,
  KPIStrip,
  PeriodControl,
  Skeleton,
  StageDrillIn,
  presetRange,
  type PeriodRange,
} from '../components';
import styles from './Dashboard.module.css';

/**
 * Management report dashboard (Board design): period control, KPI strip,
 * stage funnel, confidence baskets with drill-in, attention hints.
 * Fed by GET /api/reports/pipeline — manufacturer-only on the backend.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const { t, locale } = useT();
  const isManufacturer = user?.role === 'MANUFACTURER';

  // Default: rolling 7 days (inclusive of today).
  const [range, setRange] = useState<PeriodRange>(() => presetRange('week'));
  const [basket, setBasket] = useState<Confidence | null>(null);
  const [stage, setStage] = useState<Stage | null>(null);
  // Issue #73 — funnel product-type tabs: no «Все» (mixing industrial and
  // educational robots in one funnel misled the analyst). Two tabs only,
  // industrial route selected by default.
  const [productType, setProductType] = useState<ProductType>('INTEGRATION_PROJECT');

  const fromISO = format(range.from, 'yyyy-MM-dd');
  const toISO = format(range.to, 'yyyy-MM-dd');

  const reportQ = useQuery<PipelineReport>({
    queryKey: ['reports', 'pipeline', fromISO, toISO, productType],
    queryFn: () =>
      api.get<PipelineReport>('/reports/pipeline', {
        query: { from: fromISO, to: toISO, productType },
      }),
    enabled: isManufacturer,
  });

  if (!isManufacturer) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>{t('dashboard.title')}</h1>
            <p className={styles.subtitle}>{t('pipeline.subtitle')}</p>
          </div>
        </header>
        <EmptyState
          icon={<Lock size={26} />}
          title={t('pipeline.noAccessTitle')}
          description={t('pipeline.noAccessDesc')}
        />
      </div>
    );
  }

  const report = reportQ.data;

  // ── KPI aggregation across stages ────────────────────────────────
  const totals = report
    ? report.stages.reduce(
        (acc, s) => ({
          in: acc.in + s.in,
          out: acc.out + s.out,
          dropped: acc.dropped + s.dropped,
          paused: acc.paused + s.currentlyPaused,
        }),
        { in: 0, out: 0, dropped: 0, paused: 0 },
      )
    : null;
  const portfolioSum = report
    ? report.portfolio.K90.sum +
      report.portfolio.K50.sum +
      report.portfolio.K10.sum +
      report.portfolio.none.sum
    : 0;
  const funnelConversion = report ? funnelConversionPct(report.conversion) : 0;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('dashboard.title')}</h1>
          {report?.scope === 'own' && (
            <p className={styles.subtitle}>{t('pipeline.scopeOwnBadge')}</p>
          )}
        </div>
        <PeriodControl value={range} onChange={setRange} />
      </header>

      {reportQ.isPending && (
        <div className={styles.loading} aria-busy="true">
          <Skeleton variant="block" height={72} radius="lg" />
          <Skeleton variant="block" height={220} radius="lg" />
          <div className={styles.loadingRow}>
            <Skeleton variant="block" height={260} radius="lg" />
            <Skeleton variant="block" height={260} radius="lg" width={340} />
          </div>
        </div>
      )}

      {reportQ.isError && (
        <EmptyState
          icon={<Inbox size={26} />}
          title={t('pipeline.errorTitle')}
          description={reportQ.error.message}
          action={
            <Button variant="secondary" size="sm" onClick={() => reportQ.refetch()}>
              {t('pipeline.retry')}
            </Button>
          }
        />
      )}

      {report && totals && (
        <>
          <KPIStrip
            ariaLabel={t('pipeline.subtitle')}
            items={[
              { key: 'in', label: t('pipeline.kpiIn'), value: formatNumber(totals.in, locale) },
              {
                key: 'out',
                label: t('pipeline.kpiAdvanced'),
                value: formatNumber(totals.out, locale),
              },
              {
                key: 'dropped',
                label: t('pipeline.kpiDropped'),
                value: formatNumber(totals.dropped, locale),
                tone: 'negative',
              },
              {
                key: 'paused',
                label: t('pipeline.kpiPaused'),
                value: formatNumber(totals.paused, locale),
                tone: 'muted',
              },
              {
                key: 'conversion',
                label: t('pipeline.kpiConversionFunnel'),
                value: String(funnelConversion),
                suffix: '%',
                hint: t('pipeline.kpiConversionFunnelHint'),
              },
              {
                key: 'portfolio',
                label: t('pipeline.kpiPortfolio'),
                value: formatMoneyShort(portfolioSum, locale, t('pipeline.millionShort')),
              },
            ]}
          />

          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>{t('pipeline.funnelTitle')}</h2>
              <div className={styles.funnelTabs} role="tablist">
                {(['INTEGRATION_PROJECT', 'EDUCATIONAL_ROBOT'] as const).map((pt) => (
                  <Button
                    key={pt}
                    size="sm"
                    variant={productType === pt ? 'primary' : 'secondary'}
                    onClick={() => setProductType(pt)}
                  >
                    {pt === 'INTEGRATION_PROJECT'
                      ? t('transitions.productTypeIntegration')
                      : t('transitions.productTypeEducational')}
                  </Button>
                ))}
              </div>
            </div>
            <div className={styles.funnelCard}>
              <FunnelChart
                stages={report.stages.filter((s) => routeStages(productType).includes(s.stage))}
                productType={productType}
                conversion={report.conversion}
                baseConversion={report.baseConversion.filter((b) =>
                  routeStages(productType).includes(b.stage),
                )}
                onStageClick={setStage}
              />
            </div>
          </section>

          <div className={styles.bottomGrid}>
            <section className={styles.section}>
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>{t('pipeline.portfolioTitle')}</h2>
                <span className={styles.sectionNote}>{t('pipeline.portfolioNote')}</span>
              </div>
              <div className={styles.basketsCard}>
                <BasketPanel portfolio={report.portfolio} onSelect={setBasket} />
              </div>
            </section>
            <section className={styles.section}>
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>{t('pipeline.attentionTitle')}</h2>
                <span className={styles.sectionNote}>{t('pipeline.attentionNote')}</span>
              </div>
              <AttentionPanel report={report} productType={productType} />
            </section>
          </div>

          <BasketDrillIn
            open={basket !== null}
            basket={basket}
            from={fromISO}
            to={toISO}
            totalCount={basket ? report.portfolio[basket].count : 0}
            productType={productType}
            onClose={() => setBasket(null)}
          />
          <StageDrillIn
            open={stage !== null}
            stage={stage}
            from={fromISO}
            to={toISO}
            productType={productType}
            onClose={() => setStage(null)}
          />
        </>
      )}
    </div>
  );
}
